import axios from 'axios'

// In a Capacitor-bundled app the WebView has no dev proxy, so we need an
// absolute URL. VITE_API_URL must be set at build time for mobile releases
// (e.g. https://crm.operonlabs.com). Falls back to the relative path used
// by the web app (resolved by Nginx / Vite proxy at runtime).
const BASE_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : '/api/v1'

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,          // 30 s — prevents indefinite hangs on slow backend queries
  headers: {
    'Content-Type': 'application/json',
  },
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Refresh tokens are single-use/rotating on the backend — a second call with
// the same token is rejected as already-consumed. When the access token has
// expired (e.g. the app was inactive past its lifetime), a page's several
// parallel API calls all 401 at once; without de-duplication each one would
// race to refresh independently, and every loser would wipe storage and force
// a logout even though a sibling request just refreshed the session
// successfully. This shares one in-flight refresh across all concurrent 401s.
let refreshPromise: Promise<{ access_token: string; refresh_token?: string }> | null = null

// Retry network errors (no response at all) up to 3 times with back-off.
// This handles the common case of mobile data briefly dropping after returning
// from the dialer — disposition saves and recording uploads both hit this window.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const cfg = error.config
    if (!error.response && cfg && !cfg._networkRetry) {
      cfg._networkRetry = 1
      const maxRetries = 3
      while (cfg._networkRetry <= maxRetries) {
        const delay = cfg._networkRetry * 3_000   // 3s, 6s, 9s
        await new Promise(r => setTimeout(r, delay))
        try {
          return await api(cfg)
        } catch (retryErr: any) {
          if (retryErr.response) throw retryErr   // got a real HTTP error — stop retrying
          cfg._networkRetry++
        }
      }
    }
    if (error.response?.status === 401) {
      const refreshToken = localStorage.getItem('refresh_token')
      if (refreshToken && !error.config._retry) {
        error.config._retry = true
        try {
          if (!refreshPromise) {
            refreshPromise = axios
              .post(`${BASE_URL}/auth/refresh`, { refresh_token: refreshToken })
              .then(res => res.data)
              .finally(() => { refreshPromise = null })
          }
          const data = await refreshPromise
          localStorage.setItem('access_token', data.access_token)
          // Also persist the rotated refresh token — the old one was consumed on use
          // and will be rejected on the next refresh attempt if not replaced.
          if (data.refresh_token) {
            localStorage.setItem('refresh_token', data.refresh_token)
          }
          error.config.headers.Authorization = `Bearer ${data.access_token}`
          return api(error.config)
        } catch {
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          localStorage.removeItem('user')
          window.location.href = '/login'
        }
      }
    }
    return Promise.reject(error)
  }
)

export default api
